/**
 * CareMatrix — runtime protocol-driven click-through (Sprint H).
 *
 * When an active protocol is detected for the current ePCR (chief
 * complaint / impression match), this component renders the protocol
 * as a vertical sequence of tappable steps. Each tap:
 *
 *   1. Auto-time-stamps with Date.now()
 *   2. Runs the dose calculator (med steps) and the dose gate
 *      (blocks the click with a clinical reason when contraindicated)
 *   3. Writes the structured record to the appropriate Dexie table:
 *        med           → medications_given
 *        procedure     → procedures
 *        vital_set     → opens the vital-entry surface (deferred — for
 *                        Sprint H we mark it as "logged" with the
 *                        timestamp; the EMT enters values from the
 *                        Vitals tab. Future: inline mini-form.)
 *        assessment    → patches ePCR.set_findings.<slot>
 *        disposition   → patches ePCR (transport_decision, destination)
 *   4. Stamps protocol_id + protocol_step_order on the record so the
 *      structured trail back to the protocol is preserved (NEMSIS
 *      eProtocols.01 mapping).
 *   5. Enqueues the row for sync.
 *
 * The EMT documents BY treating, not by typing. Click → record.
 */

import { useEffect, useMemo, useState } from 'react';
import { Clock, Check, AlertTriangle, Sparkles, ChevronDown, ChevronRight, Undo2, SkipForward, X as XIcon, FileText, RefreshCw, Loader2, Plus, Edit3 } from 'lucide-react';
import { generateNarrativeFromEpcr, type AssembledDraft } from '@/modules/narrative/v2';
import { activeProtocol, type CareMatrixProtocol, type CareStep, type CalcContext, type MedStep, type ProcedureStep } from '@/lib/care-matrix';
import type { EPCR, MedicationGiven, Procedure } from '@/types';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { CONSUMERS } from '@/lib/pipeline-graph';
import { minScopeForDrug, minScopeForProcedure, scopeBlockReason } from '@/lib/scope';
import { cn } from '@/lib/cn';

interface Props {
  epcr: EPCR;
  /** ePCR-level patch routine (used for assessment/disposition steps). */
  onUpdate: (updates: Partial<EPCR>) => void;
  /**
   * Optional explicit protocol to render. Defaults to the first
   * activeProtocol(epcr) match. Used by the cockpit which renders
   * one CareMatrix per matching protocol when several are active
   * (e.g. Respiratory + Anaphylaxis on a CHF + bronchospasm patient).
   */
  protocolOverride?: CareMatrixProtocol | null;
}

const CONSUMER_BG: Record<string, string> = {
  regulatory: 'bg-red-100 text-red-800 border-red-300',
  clinical:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  department: 'bg-blue-100 text-blue-800 border-blue-300',
  financial:  'bg-amber-100 text-amber-800 border-amber-300',
};

const KIND_ACCENT: Record<CareStep['kind'], string> = {
  med:         'border-amber-400 bg-amber-50',
  procedure:   'border-violet-400 bg-violet-50',
  assessment:  'border-slate-400 bg-slate-50',
  vital_set:   'border-emerald-400 bg-emerald-50',
  disposition: 'border-purple-400 bg-purple-50',
};

const KIND_DOT: Record<CareStep['kind'], string> = {
  med:         'bg-amber-500',
  procedure:   'bg-violet-500',
  assessment:  'bg-slate-500',
  vital_set:   'bg-emerald-500',
  disposition: 'bg-purple-600',
};

/**
 * Left-edge border color per step kind on the cockpit dark plates.
 * Matches the dot color above so each step row reads as one unit. */
const KIND_BORDER: Record<CareStep['kind'], string> = {
  med:         'border-amber-500',
  procedure:   'border-violet-500',
  assessment:  'border-slate-500',
  vital_set:   'border-emerald-500',
  disposition: 'border-purple-500',
};

const KIND_LABEL: Record<CareStep['kind'], string> = {
  med:         'Medication',
  procedure:   'Procedure',
  assessment:  'Assessment',
  vital_set:   'Vital set',
  disposition: 'Disposition',
};

interface CompletedStep {
  stepId: string;
  recordId: string;
  /** What kind of record was created so undo knows which Dexie table to delete from. */
  kind: CareStep['kind'];
  timestamp: string;
}

/**
 * Step kinds where multi-tap is semantically correct: each tap records
 * a separate event (3× epi, 2× IV attempt, 4× vital sets). Assessment
 * and disposition are state declarations — one tap, not a stream of
 * events — so they remain single-tap.
 */
const MULTI_TAP_KINDS: ReadonlySet<CareStep['kind']> = new Set([
  'med', 'procedure', 'vital_set',
]);

/** A step the EMT explicitly skipped, with clinical justification. */
interface SkippedStep {
  stepId: string;
  reason: string;
  timestamp: string;
}

const SKIP_REASON_PRESETS = [
  'Patient refused',
  'Contraindicated for this patient',
  'Above scope / no resource on scene',
  'Already done by first arriving unit',
  'Online medical control directed alternative',
];

export function CareMatrix({ epcr, onUpdate, protocolOverride }: Props) {
  const { user } = useAuthStore();
  const [completed, setCompleted] = useState<CompletedStep[]>([]);
  /** Loaded medication rows so calcs can read the actual recorded
   *  dose / unit (vs the prescribed default on the step). */
  const [medsCache, setMedsCache] = useState<MedicationGiven[]>([]);
  const [error, setError]         = useState<string | null>(null);
  const [busyStep, setBusyStep]   = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  /** Steps the EMT has marked "skip with reason". Persisted into
   *  epcr.set_findings._care_matrix_skipped so reload + audit see it. */
  const [skipped, setSkipped] = useState<SkippedStep[]>(
    () => Object.values((epcr.set_findings?._care_matrix_skipped ?? {}) as Record<string, SkippedStep>),
  );
  /** When non-null, the inline skip-reason panel is open for that step id. */
  const [skipDialogStepId, setSkipDialogStepId] = useState<string | null>(null);
  const [skipReasonDraft, setSkipReasonDraft]   = useState('');

  // Narrative preview — runs the v2 engine against the live record so
  // the EMT sees the running draft assemble as they tap. Hidden by
  // default to keep the matrix focused on action; revealed by a
  // bottom-of-section toggle. Auto-regenerates whenever the
  // completed-step set changes.
  const [previewOpen, setPreviewOpen]   = useState(false);
  const [previewDraft, setPreviewDraft] = useState<AssembledDraft | null>(null);
  const [previewBusy, setPreviewBusy]   = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const protocol = useMemo(() => protocolOverride ?? activeProtocol(epcr), [epcr, protocolOverride]);

  /**
   * Cross-protocol step satisfaction.
   *
   * If the EMT taps "IV access" inside Cardiac Arrest and Stroke is
   * also an active protocol that calls for IV access, the IV step in
   * Stroke should ALSO render as satisfied — one IV access serves
   * both protocols. Same patient, same vein, same call. We scan ALL
   * medications_given + procedures on this ePCR (not just rows tagged
   * to the current protocol) and match by:
   *
   *   - meds:       case-insensitive drug_name + route match
   *   - procedures: case-insensitive procedure_type match
   *   - vital_set:  any vital_set on the ePCR (vitals are global)
   *
   * Records that DON'T match the current protocol's stepId but DO
   * match a step semantically are surfaced as "satisfied by another
   * protocol" with a small chip showing where they came from.
   */
  const [allEpcrMeds, setAllEpcrMeds] = useState<MedicationGiven[]>([]);
  const [allEpcrProcs, setAllEpcrProcs] = useState<Procedure[]>([]);
  useEffect(() => {
    let cancel = false;
    void (async () => {
      const [meds, procs] = await Promise.all([
        db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
        db.procedures.where('epcr_id').equals(epcr.id).toArray(),
      ]);
      if (cancel) return;
      setAllEpcrMeds(meds);
      setAllEpcrProcs(procs);
    })();
    return () => { cancel = true; };
  }, [epcr.id, completed.length]);

  // Hydrate the completed[] state from Dexie so a page reload (or
  // navigating away and back) shows every administration that's
  // already on the ePCR. Records are linked by protocol_id +
  // protocol_step_order, written by runStep() below.
  useEffect(() => {
    if (!protocol) return;
    let cancelled = false;
    void (async () => {
      const [meds, procs, vitals] = await Promise.all([
        db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
        db.procedures.where('epcr_id').equals(epcr.id).toArray(),
        db.vital_sets.where('epcr_id').equals(epcr.id).toArray(),
      ]);
      if (cancelled) return;
      const hydrated: CompletedStep[] = [];
      for (const m of meds) {
        if (m.protocol_id !== protocol.id || m.protocol_step_order == null) continue;
        const step = protocol.steps[m.protocol_step_order];
        if (step) hydrated.push({ stepId: step.id, recordId: m.id, kind: 'med', timestamp: m.timestamp });
      }
      for (const p of procs) {
        if (p.protocol_id !== protocol.id || p.protocol_step_order == null) continue;
        const step = protocol.steps[p.protocol_step_order];
        if (step) hydrated.push({ stepId: step.id, recordId: p.id, kind: 'procedure', timestamp: p.timestamp });
      }
      // Vital sets aren't tagged with protocol_id (the schema doesn't
      // carry it). Best-effort match: any vital_set step is satisfied
      // by the latest vital row, the rest of the timeline shows in the
      // Vitals tab. Multi-tap will still create new vital_set rows.
      void vitals;
      hydrated.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      setCompleted(hydrated);
      setMedsCache(meds);
    })();
    return () => { cancelled = true; };
  }, [protocol?.id, epcr.id]);

  // Stamp the active protocol's version onto the ePCR record once,
  // the first time the matrix activates against this ePCR. This is
  // the legal chain-of-custody anchor — even if a protocol is hot-
  // fixed years later, the ePCR resolves to the version that drove
  // the call. Stored in set_findings._protocol_versions[id] (frozen
  // map; never overwritten).
  useEffect(() => {
    if (!protocol) return;
    const cur = (epcr.set_findings?._protocol_versions ?? {}) as Record<string, { version: string; effectiveDate: string; activatedAt: string }>;
    if (cur[protocol.id]) return; // already stamped — leave alone
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        _protocol_versions: {
          ...cur,
          [protocol.id]: {
            version: protocol.version,
            effectiveDate: protocol.effectiveDate,
            activatedAt: new Date().toISOString(),
          },
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol?.id, epcr.id]);

  /**
   * Provider level for the narrative engine. Derived from the signed-
   * in user's cert; defaults to EMT if unavailable so the engine still
   * filters scope correctly.
   */
  function providerLevelForNarrative(): 'EMT' | 'AEMT' | 'AEMT-Cardiac' | 'Paramedic' {
    const c = (user?.cert_level ?? '').toString().toUpperCase();
    if (/PARAMEDIC|EMT-P\b/.test(c)) return 'Paramedic';
    if (/CARDIAC/.test(c)) return 'AEMT-Cardiac';
    if (/AEMT|EMT-I/.test(c)) return 'AEMT';
    return 'EMT';
  }

  async function regenPreview() {
    if (!protocol) return;
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const draft = await generateNarrativeFromEpcr(epcr, {
        providerLevel: providerLevelForNarrative(),
        protocolNames: [`${protocol.id} ${protocol.name}`],
      });
      setPreviewDraft(draft);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Failed to assemble narrative.');
    } finally {
      setPreviewBusy(false);
    }
  }

  // Auto-regenerate when the matrix state changes. Debounced via the
  // dependency tracking — the effect re-fires only when completed[]
  // length, skipped[] length, or the open toggle flips.
  useEffect(() => {
    if (!previewOpen) return;
    void regenPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, completed.length, skipped.length, epcr.id]);

  // Pull latest vitals + home meds for the calc context.
  const [latestVital, setLatestVital] = useState<CalcContext['latestVital']>(null);
  const [homeMeds, setHomeMeds] = useState<string[]>([]);
  useEffect(() => {
    let cancel = false;
    void (async () => {
      const vitals = await db.vital_sets.where('epcr_id').equals(epcr.id).sortBy('timestamp');
      const last = vitals[vitals.length - 1];
      if (cancel) return;
      setLatestVital(last ? {
        bp_systolic: last.bp_systolic,
        bp_diastolic: last.bp_diastolic,
        heart_rate: last.heart_rate,
        heart_rhythm: last.heart_rhythm,
        respiratory_rate: last.respiratory_rate,
        spo2: last.spo2,
        etco2: last.etco2,
        blood_glucose: last.blood_glucose,
        gcs_total: last.gcs_total,
      } : null);
      // Home meds — read from any field that might hold them. The
      // canonical slot evolves; tolerate either.
      const m = (epcr as unknown as { medications?: string[]; home_medications?: string[] }).medications
              ?? (epcr as unknown as { medications?: string[]; home_medications?: string[] }).home_medications
              ?? [];
      setHomeMeds(m);
    })();
    return () => { cancel = true; };
  }, [epcr.id]);

  if (!protocol) return null;

  /**
   * Per-step administration history loaded into Dexie. The CalcContext
   * passes `history` to dose calculators so they can render running
   * totals + interval timers. We build a Promise-resolving lookup once
   * per render so the calcs in the JSX render synchronously off cached
   * data.
   */
  const historyByStep: Record<string, CalcContext['history']> = {};
  for (const c of completed) {
    (historyByStep[c.stepId] ??= []).push({ timestamp: c.timestamp });
  }
  // For med steps, enrich history entries with dose + unit so the
  // cumulative-total math can run. Pull from in-memory medsCache when
  // available; falls back to the step's prescribed dose otherwise.
  for (const step of protocol.steps) {
    if (step.kind !== 'med') continue;
    const h = historyByStep[step.id];
    if (!h) continue;
    for (let i = 0; i < h.length; i += 1) {
      const recId = completed.find((c) => c.stepId === step.id && c.timestamp === h[i].timestamp)?.recordId;
      const med = medsCache.find((m) => m.id === recId);
      h[i] = {
        timestamp: h[i].timestamp,
        dose: med?.dose ?? step.dose,
        unit: med?.unit ?? step.unit,
      };
    }
  }

  function ctxFor(stepId: string): CalcContext {
    return {
      epcr,
      latestVital,
      weight_kg: epcr.peds_weight_kg ?? null,
      hasMed: (rx) => homeMeds.some((m) => rx.test(m)),
      history: historyByStep[stepId] ?? [],
    };
  }

  async function runStep(step: CareStep, stepIndex: number) {
    if (!user) return;
    setError(null);
    setBusyStep(step.id);
    try {
      // Gate check: provider scope (med + procedure) + clinical dose
      // gate (med). Scope is enforced as a HARD block per Sprint K —
      // not a warning. EMT cannot insert paramedic-only meds, etc.
      if (step.kind === 'med') {
        const scope = scopeBlockReason(user.cert_level, minScopeForDrug(step.drug_name));
        if (scope) { setError(`${step.label}: ${scope}`); return; }
        const block = step.doseGate?.(ctxFor(step.id));
        if (block) { setError(`${step.label}: ${block}`); return; }
      } else if (step.kind === 'procedure') {
        const scope = scopeBlockReason(user.cert_level, minScopeForProcedure(step.procedure_type));
        if (scope) { setError(`${step.label}: ${scope}`); return; }
      }

      const ts = new Date().toISOString();
      let recordId: string;

      switch (step.kind) {
        case 'med': {
          const id = crypto.randomUUID();
          const row: MedicationGiven = {
            id,
            epcr_id: epcr.id,
            drug_name: step.drug_name,
            dose: step.dose,
            unit: step.unit,
            route: step.route,
            timestamp: ts,
            performed_by: user.id,
            patient_response: null,
            controlled_substance: false,
            waste_documented: false,
            waste_witness: null,
            protocol_id: protocol!.id,
            protocol_step_order: stepIndex,
            is_deviation: false,
            deviation_reason: null,
            created_at: ts,
          };
          await db.medications_given.add(withSyncMeta(row));
          enqueueSync('medications_given', id, 'insert', row as unknown as Record<string, unknown>);
          setMedsCache((prev) => [...prev, row]);
          recordId = id;
          break;
        }
        case 'procedure': {
          const id = crypto.randomUUID();
          const row: Procedure = {
            id,
            epcr_id: epcr.id,
            timestamp: ts,
            procedure_type: step.procedure_type,
            body_site: step.body_site ?? null,
            performed_by: user.id,
            attempt_number: 1,
            outcome: 'successful',
            complications: null,
            protocol_id: protocol!.id,
            protocol_step_order: stepIndex,
            is_deviation: false,
            deviation_reason: null,
            created_at: ts,
          };
          await db.procedures.add(withSyncMeta(row));
          enqueueSync('procedures', id, 'insert', row as unknown as Record<string, unknown>);
          recordId = id;
          break;
        }
        case 'assessment': {
          // Patch ePCR.set_findings.<slot> with the step's fixed payload.
          recordId = `assess-${step.id}`;
          if (step.setFindings) {
            const cur = epcr.set_findings ?? {};
            const slot = step.setFindings.slot;
            onUpdate({
              set_findings: {
                ...cur,
                [slot]: { ...(cur[slot] ?? {}), ...step.setFindings.patch, noted_at: ts },
              },
            });
          }
          break;
        }
        case 'vital_set': {
          // Sprint H: write a vital-set placeholder with timestamp. EMT
          // fills numbers from the Vitals tab. Future: inline mini-form.
          const id = crypto.randomUUID();
          await db.vital_sets.add(withSyncMeta({
            id,
            epcr_id: epcr.id,
            timestamp: ts,
            source: 'manual',
            bp_systolic: null,
            bp_diastolic: null,
            heart_rate: null,
            heart_rhythm: null,
            respiratory_rate: null,
            spo2: null,
            etco2: null,
            gcs_total: null,
            gcs_eye: null,
            gcs_verbal: null,
            gcs_motor: null,
            temperature: null,
            blood_glucose: null,
            pain_scale: null,
            created_at: ts,
          }));
          recordId = id;
          break;
        }
        case 'disposition': {
          recordId = `disp-${step.id}`;
          if (step.patch) onUpdate(step.patch);
          break;
        }
      }

      setCompleted((prev) => [...prev, { stepId: step.id, recordId, kind: step.kind, timestamp: ts }]);
      // If this step had been previously skipped (rare but possible —
      // EMT changed their mind and is now performing it), drop the
      // skip marker so the row doesn't render as "skipped + done".
      if (skipped.some((s) => s.stepId === step.id)) {
        unskipStep(step.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record step.');
    } finally {
      setBusyStep(null);
    }
  }

  /**
   * Untap — remove the structured record + clear the completion mark.
   * Critical for medical defensibility: an EMT who taps the wrong step
   * must be able to back out without leaving a phantom drug/procedure
   * record on the ePCR. The Dexie row is deleted and a delete is queued
   * for sync. Assessment + disposition steps are stickier because they
   * mutated the ePCR document; we clear the completion marker but do
   * NOT auto-revert the patch (the EMT can edit those fields directly
   * on the relevant tab).
   */
  /**
   * Edit the timestamp on a multi-tap record. Uses a native time
   * prompt — quick + universally supported. The recorded time on the
   * underlying Dexie row is updated and synced. Per-state and federal
   * mandates require accurate intervention timestamps; this lets the
   * EMT correct a tap that landed at the wrong moment (e.g., realized
   * 30 sec later that the actual administration was earlier than the
   * tap).
   */
  async function editRecordTime(step: CareStep, record: CompletedStep) {
    const cur = new Date(record.timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    const initial = `${pad(cur.getHours())}:${pad(cur.getMinutes())}:${pad(cur.getSeconds())}`;
    const input = window.prompt(
      `Correct timestamp (HH:MM or HH:MM:SS, 24-hour). Currently ${initial}.`,
      initial,
    );
    if (!input) return;
    const m = input.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) {
      setError(`Invalid time format "${input}". Use HH:MM or HH:MM:SS (24-hour).`);
      return;
    }
    const next = new Date(cur);
    next.setHours(Number(m[1]), Number(m[2]), Number(m[3] ?? '0'), 0);
    const ts = next.toISOString();
    setBusyStep(step.id);
    try {
      switch (record.kind) {
        case 'med': {
          await db.medications_given.update(record.recordId, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('medications_given', record.recordId, 'update', { id: record.recordId, timestamp: ts });
          setMedsCache((prev) => prev.map((m2) => m2.id === record.recordId ? { ...m2, timestamp: ts } : m2));
          break;
        }
        case 'procedure': {
          await db.procedures.update(record.recordId, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('procedures', record.recordId, 'update', { id: record.recordId, timestamp: ts });
          break;
        }
        case 'vital_set': {
          await db.vital_sets.update(record.recordId, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('vital_sets', record.recordId, 'update', { id: record.recordId, timestamp: ts });
          break;
        }
        default:
          break;
      }
      setCompleted((prev) =>
        prev.map((c) => c.recordId === record.recordId ? { ...c, timestamp: ts } : c),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update timestamp.');
    } finally {
      setBusyStep(null);
    }
  }

  async function untapStepRecord(step: CareStep, record: CompletedStep) {
    setError(null);
    setBusyStep(step.id);
    try {
      switch (record.kind) {
        case 'med': {
          await db.medications_given.delete(record.recordId);
          enqueueSync('medications_given', record.recordId, 'delete', { id: record.recordId });
          setMedsCache((prev) => prev.filter((m) => m.id !== record.recordId));
          break;
        }
        case 'procedure': {
          await db.procedures.delete(record.recordId);
          enqueueSync('procedures', record.recordId, 'delete', { id: record.recordId });
          break;
        }
        case 'vital_set': {
          await db.vital_sets.delete(record.recordId);
          enqueueSync('vital_sets', record.recordId, 'delete', { id: record.recordId });
          break;
        }
        case 'assessment':
        case 'disposition':
          // Marker only — patches into ePCR.set_findings / disposition
          // are not auto-reverted. EMT edits the relevant tab directly.
          break;
      }
      setCompleted((prev) => prev.filter((c) => c.recordId !== record.recordId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo step.');
    } finally {
      setBusyStep(null);
    }
  }

  /**
   * Skip with reason — records that the EMT consciously chose NOT to
   * perform a protocol-prescribed step. The reason is the audit trail.
   * Persisted into epcr.set_findings._care_matrix_skipped[stepId] so
   * it survives reload, prints in the audit report, and is visible
   * to QA review.
   */
  function skipStep(step: CareStep, reason: string) {
    const trimmed = reason.trim();
    if (!trimmed) return;
    const ts = new Date().toISOString();
    const next: SkippedStep = { stepId: step.id, reason: trimmed, timestamp: ts };
    setSkipped((prev) => {
      const without = prev.filter((s) => s.stepId !== step.id);
      return [...without, next];
    });
    persistSkippedToEpcr({ ...indexSkipped(skipped.filter((s) => s.stepId !== step.id)), [step.id]: next });
    setSkipDialogStepId(null);
    setSkipReasonDraft('');
  }

  function unskipStep(stepId: string) {
    setSkipped((prev) => prev.filter((s) => s.stepId !== stepId));
    const remaining = indexSkipped(skipped.filter((s) => s.stepId !== stepId));
    persistSkippedToEpcr(remaining);
  }

  function indexSkipped(arr: SkippedStep[]): Record<string, SkippedStep> {
    return Object.fromEntries(arr.map((s) => [s.stepId, s]));
  }

  function persistSkippedToEpcr(map: Record<string, SkippedStep>) {
    const cur = epcr.set_findings ?? {};
    onUpdate({
      set_findings: {
        ...cur,
        _care_matrix_skipped: map,
      },
    });
  }

  // Distinct steps that have at least one completed record.
  const distinctCompletedStepIds = new Set(completed.map((c) => c.stepId));
  const completedCount = distinctCompletedStepIds.size;
  const total = protocol.steps.length;

  return (
    <section className="rail-cockpit relative overflow-hidden">
      <span aria-hidden className="absolute inset-x-0 top-0 h-1 accent-stripe-fire" />
      {/* Header — engraved metal plate identity for the active protocol. */}
      <header className="border-b border-[var(--cockpit-edge-deep)] px-3 py-2 flex items-center gap-3">
        <Sparkles size={18} className="text-amber-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)] flex items-center gap-2">
            <span>Active protocol</span>
            {/* Provenance badge — handbuilt = vitals-aware audited
                gates; engine = converted from JSON, contraindications
                are warnings only. Surfaces to QA who's reviewing what. */}
            <span className={cn(
              'inline-block rounded px-1 py-px text-[8px] font-bold tracking-widest',
              protocol.provenance === 'handbuilt'
                ? 'bg-emerald-700 text-white'
                : 'bg-slate-500 text-white',
            )}>
              {protocol.provenance === 'handbuilt' ? 'AUDITED' : 'AUTO-CONVERTED'}
            </span>
          </div>
          <h3 className="font-bold text-[var(--cockpit-text-on-panel)] truncate">{protocol.name}</h3>
          <div className="text-[11px] text-[var(--cockpit-text-on-panel-2)] truncate flex items-center gap-2 flex-wrap">
            <span>{protocol.citation}</span>
            <span className="font-mono text-[10px] text-amber-300 bg-black/40 border border-[var(--cockpit-edge-deep)] rounded px-1 py-px">
              v{protocol.version} · eff {protocol.effectiveDate}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">progress</div>
          <div className="font-bold text-[var(--cockpit-text-on-panel)] tabular-nums">{completedCount} / {total}</div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand care matrix' : 'Collapse care matrix'}
          className="rail-button ml-1 p-1.5 min-h-touch min-w-touch flex items-center justify-center"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
      </header>

      {!collapsed && (
        <div className="p-3 space-y-2 bg-[color:var(--cockpit-canvas)]">
          {error && (
            <div className="border border-red-700 bg-red-950/60 rounded px-3 py-2 text-sm text-red-200 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <ol className="relative pl-6 space-y-2">
            <span aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-[var(--cockpit-edge-deep)]" />
            {protocol.steps.map((step, i) => {
              // Direct records — tapped inside this very step.
              const directRecords = completed.filter((c) => c.stepId === step.id);
              // Cross-protocol satisfaction — records on the same ePCR
              // that semantically match this step but were tagged to a
              // DIFFERENT protocol step. These display as "satisfied
              // elsewhere" so the EMT doesn't double-tap and create
              // duplicate records.
              const crossProtocolMatches: Array<{ recordId: string; timestamp: string; sourceLabel: string }> = [];
              if (step.kind === 'med') {
                const ms = step as MedStep;
                for (const m of allEpcrMeds) {
                  if (directRecords.some((d) => d.recordId === m.id)) continue;
                  if (m.drug_name?.toUpperCase() === ms.drug_name.toUpperCase()
                      && m.route?.toUpperCase() === ms.route.toUpperCase()) {
                    crossProtocolMatches.push({
                      recordId: m.id,
                      timestamp: m.timestamp,
                      sourceLabel: m.protocol_id ?? 'free entry',
                    });
                  }
                }
              } else if (step.kind === 'procedure') {
                const ps = step as ProcedureStep;
                for (const p of allEpcrProcs) {
                  if (directRecords.some((d) => d.recordId === p.id)) continue;
                  if (p.procedure_type?.toUpperCase() === ps.procedure_type.toUpperCase()) {
                    crossProtocolMatches.push({
                      recordId: p.id,
                      timestamp: p.timestamp,
                      sourceLabel: p.protocol_id ?? 'free entry',
                    });
                  }
                }
              }
              const stepRecords = directRecords.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
              const done = stepRecords[0] ?? (crossProtocolMatches[0] ? {
                stepId: step.id,
                recordId: crossProtocolMatches[0].recordId,
                kind: step.kind,
                timestamp: crossProtocolMatches[0].timestamp,
              } as CompletedStep : undefined);
              const isMultiTap = MULTI_TAP_KINDS.has(step.kind);
              const hasCrossSatisfaction = crossProtocolMatches.length > 0;
              const skip = skipped.find((s) => s.stepId === step.id);
              const busy = busyStep === step.id;
              const skipOpen = skipDialogStepId === step.id;
              // Combine clinical dose gate with provider scope gate.
              // Scope is checked for med + procedure steps; vital_set
              // and assessment have no scope gating.
              const doseBlock = step.kind === 'med' ? step.doseGate?.(ctxFor(step.id)) ?? null : null;
              const scopeBlock =
                step.kind === 'med'        ? scopeBlockReason(user?.cert_level, minScopeForDrug(step.drug_name))
                : step.kind === 'procedure' ? scopeBlockReason(user?.cert_level, minScopeForProcedure(step.procedure_type))
                : null;
              const blocked = doseBlock ?? scopeBlock;
              const calc    = step.kind === 'med' ? step.doseCalc?.(ctxFor(step.id)) ?? null : null;

              return (
                <li key={step.id} className="relative">
                  <span aria-hidden className={cn(
                    'absolute -left-6 top-2.5 inline-block w-3 h-3 rounded-full ring-2 ring-[var(--cockpit-canvas)]',
                    done ? 'bg-emerald-500' : skip ? 'bg-amber-500' : KIND_DOT[step.kind],
                  )} />

                  {/* Step plate — gunmetal panel, colored left-edge stripe
                      indicates kind/status. Replaces the previous bright-
                      pastel cards with the cockpit dark-canvas look. */}
                  <div className={cn(
                    'rounded px-3 py-2 space-y-1.5 metal-bevel',
                    'bg-[color:var(--cockpit-panel)] border-l-4',
                    done ? 'border-emerald-500'
                      : skip ? 'border-amber-500'
                      : KIND_BORDER[step.kind],
                    blocked && !done && !skip && 'border-red-500',
                  )}>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">
                        {KIND_LABEL[step.kind]}
                        {isMultiTap && stepRecords.length > 1 && (
                          <span className="ml-1.5 text-emerald-300">×{stepRecords.length}</span>
                        )}
                      </span>
                      <span className="font-semibold text-sm text-[var(--cockpit-text-on-panel)] flex-1 min-w-0">
                        {step.label}
                      </span>
                      {done && !isMultiTap && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300">
                          <Clock size={10} />
                          {new Date(done.timestamp).toLocaleTimeString()}
                        </span>
                      )}
                    </div>

                    {step.hint && (
                      <p className="text-[11px] text-[var(--cockpit-text-on-panel-2)] leading-snug">{step.hint}</p>
                    )}

                    {calc && !blocked && (
                      <div className="text-[11px] text-amber-200 italic flex items-center gap-1.5 bg-black/30 border border-amber-900/40 rounded px-2 py-1">
                        <span className="text-amber-400 font-mono">∑</span>
                        {calc}
                      </div>
                    )}
                    {blocked && !done && (
                      <div className="text-[11px] text-red-200 bg-red-950/60 border border-red-700 rounded px-2 py-1.5 flex items-start gap-1.5">
                        <AlertTriangle size={12} className="text-red-400 shrink-0 mt-0.5" />
                        <span>
                          <strong>{scopeBlock && !doseBlock ? 'Above scope:' : 'Withhold:'}</strong>{' '}
                          {blocked}
                        </span>
                      </div>
                    )}

                    {/* Cross-protocol satisfaction chip — shows when
                        the same intervention was already recorded
                        under a different protocol or as a free entry.
                        Saves the EMT from double-tapping the same IV
                        twice across protocols. */}
                    {hasCrossSatisfaction && stepRecords.length === 0 && (
                      <div className="text-[11px] text-sky-200 bg-sky-950/60 border border-sky-700 rounded px-2 py-1.5 flex items-start gap-1.5">
                        <Check size={12} className="text-sky-300 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <strong>Already satisfied</strong> by {crossProtocolMatches.length} matching {crossProtocolMatches.length === 1 ? 'entry' : 'entries'} elsewhere on this ePCR
                          {crossProtocolMatches.slice(0, 2).map((m) => (
                            <span key={m.recordId} className="ml-1 font-mono text-[10px] text-sky-300">
                              · {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                          ))}
                          . No need to re-record.
                        </div>
                      </div>
                    )}

                    {/* Multi-tap administration timeline — for med /
                        procedure / vital_set steps where each tap
                        creates a separate record. Each entry has its
                        own undo. Single-tap kinds skip this block. */}
                    {isMultiTap && stepRecords.length > 0 && (
                      <ul className="space-y-1 pt-1">
                        {stepRecords.map((rec, idx) => (
                          <li key={rec.recordId} className="flex items-center gap-2 text-[12px] bg-black/30 border border-emerald-900/40 rounded px-2 py-1.5">
                            <span className="font-mono text-emerald-300 tabular-nums shrink-0">
                              #{idx + 1}
                            </span>
                            <Clock size={11} className="text-emerald-400 shrink-0" />
                            <span className="font-mono text-emerald-100 tabular-nums font-bold shrink-0">
                              {new Date(rec.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                            </span>
                            <span className="flex-1 text-[var(--cockpit-text-on-panel-2)] truncate">
                              {step.kind === 'med'
                                ? `${(step as MedStep).drug_name} ${(step as MedStep).dose} ${(step as MedStep).unit} ${(step as MedStep).route}`
                                : step.kind === 'procedure'
                                  ? (step as ProcedureStep).procedure_type
                                  : 'Vital set'}
                            </span>
                            {!epcr.locked && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => editRecordTime(step, rec)}
                                  className="rail-button-mini text-sky-300 hover:text-sky-100"
                                  title="Correct this timestamp"
                                  aria-label="Edit timestamp"
                                  disabled={busy}
                                >
                                  <Edit3 size={11} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => untapStepRecord(step, rec)}
                                  className="rail-button-mini text-amber-300 hover:text-amber-100"
                                  title="Remove this entry"
                                  disabled={busy}
                                >
                                  <Undo2 size={11} />
                                </button>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Fan-out chips */}
                    <div className="flex flex-wrap items-center gap-1 pt-0.5">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)] mr-0.5">
                        feeds {step.feeds.length} →
                      </span>
                      {step.feeds.map((id) => {
                        const c = CONSUMERS[id];
                        return (
                          <span
                            key={id}
                            title={`${c.name}: ${c.rationale}`}
                            className={cn(
                              'inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border',
                              CONSUMER_BG[c.category],
                            )}
                          >
                            {c.short}
                          </span>
                        );
                      })}
                    </div>

                    {step.triggers?.map((t) => (
                      <div
                        key={t.consumer}
                        className="flex items-baseline gap-1.5 text-[11px] text-amber-200 mt-1.5 pt-1.5 border-t border-dashed border-amber-700"
                      >
                        <span className="text-amber-400 font-mono">⤳</span>
                        <span>
                          <strong>Triggers:</strong> {CONSUMERS[t.consumer].name}, {t.label}
                        </span>
                      </div>
                    ))}

                    {/* Skip marker — shown when this step has a recorded
                        skip reason. Audit-visible amber callout. */}
                    {skip && !done && (
                      <div className="flex items-start gap-1.5 text-[11px] text-amber-200 bg-amber-950/60 border border-amber-700 rounded px-2 py-1.5">
                        <SkipForward size={12} className="text-amber-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <strong>Skipped:</strong> {skip.reason}
                          <span className="ml-2 font-mono text-[10px] text-amber-400">
                            {new Date(skip.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {!epcr.locked && (
                          <button
                            type="button"
                            onClick={() => unskipStep(step.id)}
                            className="text-amber-300 hover:text-amber-100 shrink-0"
                            title="Undo skip"
                          >
                            <XIcon size={12} />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Inline skip-reason panel — cockpit-themed dark form
                        with white-inset textarea per the rest of the app. */}
                    {skipOpen && !done && !skip && (
                      <div className="border border-amber-700 bg-amber-950/40 rounded p-2 space-y-2">
                        <div className="text-[11px] font-semibold text-amber-200">
                          Why is this step not being performed?
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {SKIP_REASON_PRESETS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setSkipReasonDraft((r) => r ? `${r}; ${preset}` : preset)}
                              className="rail-button px-2 py-0.5 text-[11px]"
                            >
                              + {preset}
                            </button>
                          ))}
                        </div>
                        <textarea
                          value={skipReasonDraft}
                          onChange={(e) => setSkipReasonDraft(e.target.value)}
                          rows={2}
                          autoFocus
                          placeholder="Clinical justification (required)…"
                          className="input-field w-full text-[12px]"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={!skipReasonDraft.trim()}
                            onClick={() => skipStep(step, skipReasonDraft)}
                            className="rail-button rail-button-active px-3 py-1 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save skip
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSkipDialogStepId(null);
                              setSkipReasonDraft('');
                            }}
                            className="rail-button px-3 py-1 text-[12px]"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action row — cockpit metal buttons. Primary tap
                        always available for multi-tap kinds (med /
                        procedure / vital_set) so 3 epis = 3 taps; only
                        single-tap kinds (assessment / disposition) flip
                        to a "Recorded" pill after first tap. All
                        buttons share the .rail-action / .rail-button-sm
                        sizing so the matrix reads as one toolkit. */}
                    <div className="pt-1 flex flex-wrap items-center gap-2">
                      {(!done || isMultiTap) && !skip && (
                        <button
                          type="button"
                          disabled={busy || epcr.locked || (step.kind === 'med' && !!blocked)}
                          onClick={() => runStep(step, i)}
                          className={cn(
                            'rail-button rail-action',
                            blocked && !done && 'opacity-40 cursor-not-allowed',
                          )}
                        >
                          {busy
                            ? 'Recording…'
                            : isMultiTap && stepRecords.length > 0
                              ? <><Plus size={14} /> Record next dose</>
                              : step.kind === 'med'
                                ? 'Tap to administer'
                                : step.kind === 'procedure'
                                  ? 'Tap to record'
                                  : step.kind === 'assessment'
                                    ? 'Tap to confirm finding'
                                    : step.kind === 'vital_set'
                                      ? 'Tap to log timestamp'
                                      : 'Tap to commit'}
                        </button>
                      )}

                      {done && !isMultiTap && (
                        <>
                          <span className="rail-action bg-emerald-700 text-white border border-emerald-900 rounded">
                            <Check size={14} /> Recorded
                          </span>
                          {!epcr.locked && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => untapStepRecord(step, done)}
                              className="rail-button rail-button-sm disabled:opacity-50"
                              title="Remove this entry — deletes the structured record"
                            >
                              <Undo2 size={12} /> Undo
                            </button>
                          )}
                        </>
                      )}

                      {!done && !skip && !skipOpen && !epcr.locked && (
                        <button
                          type="button"
                          onClick={() => {
                            setSkipDialogStepId(step.id);
                            setSkipReasonDraft('');
                          }}
                          className="rail-button rail-button-sm text-amber-200"
                          title="Mark this step as not done with a clinical reason"
                        >
                          <SkipForward size={12} /> Skip — reason…
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Narrative preview — runs the v2 engine against the live
              ePCR every time the matrix changes. Lets the EMT see the
              draft assembling as they treat, instead of waiting until
              the Narrative tab to find out a slot is empty. */}
          <section className="border-t border-[var(--cockpit-edge-deep)] pt-2">
            <button
              type="button"
              onClick={() => setPreviewOpen((o) => !o)}
              className="flex w-full items-center gap-2 text-[12px] font-semibold hover:bg-black/20 rounded px-1 py-0.5"
              aria-expanded={previewOpen}
            >
              <FileText size={14} className="text-amber-400" />
              <span className="text-[var(--cockpit-text-on-panel)]">Live narrative preview</span>
              {previewBusy && <Loader2 size={12} className="animate-spin text-[var(--cockpit-text-on-panel-3)]" />}
              {previewDraft && !previewBusy && (
                <span className="font-mono text-[10px] text-[var(--cockpit-text-on-panel-3)]">
                  {previewDraft.format} · {previewDraft.fullText.length} chars
                </span>
              )}
              <span className="ml-auto text-[var(--cockpit-text-on-panel-3)]">
                {previewOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            </button>

            {previewOpen && (
              <div className="mt-2 space-y-2">
                {previewError && (
                  <div className="border border-red-700 bg-red-950/60 rounded px-3 py-2 text-sm text-red-200 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
                    <span>{previewError}</span>
                  </div>
                )}

                {previewDraft && (
                  <>
                    {/* Validation summary — failures + warnings from the
                        narrative engine's slot validator. */}
                    {(previewDraft.validation.failures.length > 0 ||
                      previewDraft.validation.warnings.length > 0) && (
                      <div className="border border-amber-700 bg-amber-950/40 rounded px-3 py-2 text-[11px] text-amber-200 space-y-1">
                        {previewDraft.validation.failures.map((f, i) => (
                          <div key={`f${i}`} className="flex items-start gap-1.5">
                            <AlertTriangle size={11} className="text-red-400 shrink-0 mt-0.5" />
                            <span><strong>Missing:</strong> {f}</span>
                          </div>
                        ))}
                        {previewDraft.validation.warnings.map((w, i) => (
                          <div key={`w${i}`} className="flex items-start gap-1.5">
                            <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-0.5" />
                            <span>{w}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* The draft itself — white inset matching the rest of
                        the cockpit's input fields. Reads as a "screen" set
                        into the gunmetal panel. */}
                    <pre
                      className="bg-white border border-[var(--cockpit-edge-deep)] rounded p-3 text-[13px] leading-relaxed whitespace-pre-wrap max-h-[40vh] overflow-y-auto font-sans shadow-inner"
                      style={{ color: '#0e1218' }}
                    >
                      {previewDraft.fullText}
                    </pre>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={regenPreview}
                        disabled={previewBusy}
                        className="rail-button inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50"
                      >
                        <RefreshCw size={11} className={previewBusy ? 'animate-spin' : ''} /> Regenerate
                      </button>
                      <span className="text-[10px] text-[var(--cockpit-text-on-panel-3)] font-mono">
                        Auto-refreshes when you tap, undo, or skip a step.
                      </span>
                    </div>
                  </>
                )}

                {!previewDraft && !previewBusy && !previewError && (
                  <div className="text-[11px] text-[var(--cockpit-text-on-panel-3)] italic">
                    Loading preview…
                  </div>
                )}
              </div>
            )}
          </section>

          <p className="text-[11px] text-[var(--cockpit-text-on-panel-3)] pt-2 leading-snug border-t border-[var(--cockpit-edge-deep)]">
            Each tap auto-stamps the timestamp, runs the dose calc, and writes
            the structured record (medication / procedure / assessment / vital
            set / disposition) tagged with this protocol id and step order.
            Multi-tap kinds (medication / procedure / vital set) record a new
            entry every tap — three epis, three taps, three timestamps. EMT
            documents by treating, not by typing.
          </p>
        </div>
      )}
    </section>
  );
}
